import * as React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { ITranslationsCollection, translate } from 'elc-service';
import * as classnames from 'classnames';
import { Heading5 } from 'elc-typography';
import { INumRecs, IReplacements } from '../interfaces/ISearchInterfaces';
import {
    PRODUCTS_IN_OVERLAY,
    RESULTS_MESSAGE,
    SEARCH_TERM,
    RECS_COUNT,
    RESULTS_COUNT,
    ADJUSTED_SEARCH_TERM,
    SEARCH_VIEW
} from '../constants/Search';
import {
    searchResultsHeader,
    bestSellerTitleMobile,
    filterHeadingTheme,
    bestSellerTitle
} from '../../theme/default-theme';
import { ContentSpacing, breakpoint } from 'elc-base-theme';

const {
    RESULTS_FOUND,
    RESULTS_FOUND_OVERLAY,
    NO_RESULTS,
    RESULTS_ADJUSTMENT,
    NONE
} = RESULTS_MESSAGE;

const ResultsMessageWrapper = styled.div`
    padding: 0;
`;

export interface ISearchResults {
    inputValue: string;
    resultsLoaded: boolean;
    translations?: ITranslationsCollection;
    numRecs: INumRecs;
    resultType: number;
    contentRecs?: number;
    adjustedTerm?: string;
    view: number;
}

const SearchTermWrapper = styled.span`
    ${searchResultsHeader};
`;

const SearchedTermWrapper = styled.span``;

const TitleWrapper = styled(Heading5)`
    text-align: left;
    padding: ${ContentSpacing.space16} 0;
    ${bestSellerTitleMobile};
    ${filterHeadingTheme};
    [dir='rtl'] & {
        text-align: right;
    }

    ${breakpoint('desktop')`
        text-align: center;
        padding: ${ContentSpacing.space16} 0 ${ContentSpacing.space24} 0;
        ${bestSellerTitle};
        [dir='rtl'] & {
            text-align: center;
        }
    `};
`;

const SearchTerm = styled.p``;

@translate([
    'noResultsMessage',
    'bestSellersProductsTitle',
    'resultsFoundMessage',
    'resultsFoundMessageOverlay',
    'searchAdjustmentMessage'
])
@observer
export class SearchResultsMessage extends React.Component<ISearchResults> {
    private getViewType = (type: number) => {
        return SEARCH_VIEW.OVERLAY === type ? 'Overlay' : 'Results';
    };
    // Get translated string after replacing the keywords with the corresponding values
    private getMessageAndClasses = () => {
        const {
            resultsFoundMessage,
            resultsFoundMessageOverlay,
            noResultsMessage,
            searchAdjustmentMessage
        } = this.props.translations as ITranslationsCollection;

        const {
            inputValue = '',
            numRecs: { totalNumRecs },
            contentRecs,
            adjustedTerm,
            resultType,
            view
        } = this.props;

        let lastRecNum = this.props.numRecs.lastRecNum;

        let searchResultMessageClassNames = '';
        let message = '';
        let dataTestId = '';
        let parts;

        if (contentRecs) {
            lastRecNum = lastRecNum - contentRecs;
        }

        // Keywords (in translatable fields) and values mapping
        const replacements: IReplacements = {
            [SEARCH_TERM]: inputValue,
            [RECS_COUNT]:
                lastRecNum && lastRecNum >= PRODUCTS_IN_OVERLAY ? PRODUCTS_IN_OVERLAY : lastRecNum,
            [RESULTS_COUNT]: totalNumRecs,
            [ADJUSTED_SEARCH_TERM]: adjustedTerm || ''
        };

        // A regEx containing all the keywords in the translatable fields
        const regEx = new RegExp(Object.keys(replacements).join('|'), 'gi');

        switch (resultType) {
            case RESULTS_FOUND:
                searchResultMessageClassNames = classnames(
                    'elc-search-result-page-message',
                    'js-search-result-page-message'
                );
                dataTestId = `SEA-Display-Message-${this.getViewType(view)}`;
                const searchedTermClassNames = classnames(
                    'elc-search-searched-term-wrapper',
                    'js-search-searched-term-wrapper'
                );
                parts = resultsFoundMessage.split(new RegExp(`(${SEARCH_TERM})`, 'gi'));
                message = parts.map((part: string) => {
                    if (part === SEARCH_TERM) {
                        return (
                            <SearchedTermWrapper
                                key="searched-term"
                                className={searchedTermClassNames}
                            >
                                {inputValue}
                            </SearchedTermWrapper>
                        );
                    } else if (part.includes(RESULTS_COUNT)) {
                        return part.replace(RESULTS_COUNT, replacements[RESULTS_COUNT].toString());
                    }

                    return part;
                });
                break;
            case RESULTS_ADJUSTMENT:
                searchResultMessageClassNames = classnames(
                    'elc-search-result-adjustment-message',
                    'js-search-result-adjustment-message'
                );
                dataTestId = `SEA-Display-Message-Spell-Check-${this.getViewType(view)}`;
                message = searchAdjustmentMessage.replace(regEx, (matched: keyof IReplacements) => {
                    return replacements[matched];
                });
                break;
            case RESULTS_FOUND_OVERLAY:
                searchResultMessageClassNames = classnames(
                    'elc-search-result-overlay-message',
                    'js-search-result-overlay-message'
                );
                dataTestId = `SEA-Display-Message-${this.getViewType(view)}`;
                message = resultsFoundMessageOverlay.replace(
                    regEx,
                    (matched: keyof IReplacements) => {
                        return replacements[matched];
                    }
                );
                break;
            case NO_RESULTS:
                searchResultMessageClassNames = classnames(
                    'elc-search-no-result-message',
                    'js-search-no-result-message'
                );
                dataTestId = `SEA-NoResultsMessage-${this.getViewType(view)}`;
                const searchTermClassNames = classnames(
                    'elc-search-search-term-wrapper',
                    'js-search-search-term-wrapper'
                );
                parts = noResultsMessage.split(new RegExp(`(${SEARCH_TERM})`, 'gi'));
                message = parts.map((part: string) =>
                    part === SEARCH_TERM ? (
                        <SearchTermWrapper
                            key="no-results"
                            data-test-id={`SEA-NoResultsKeyword-${this.getViewType(view)}`}
                            className={searchTermClassNames}
                        >
                            {inputValue}
                        </SearchTermWrapper>
                    ) : (
                        part
                    )
                );
                break;
            default:
                return {
                    searchResultMessageClassNames,
                    message,
                    dataTestId
                };
        }

        return {
            searchResultMessageClassNames,
            message,
            dataTestId
        };
    };

    public render() {
        const { bestSellersProductsTitle } = this.props.translations as ITranslationsCollection;
        const resultsMessageWrapperClassNames = classnames(
            'elc-search-results-message-wrapper',
            'js-search-results-message-wrapper'
        );

        const { resultType, resultsLoaded } = this.props;

        const { searchResultMessageClassNames, message, dataTestId } = this.getMessageAndClasses();

        return resultsLoaded && resultType === NONE ? null : (
            <ResultsMessageWrapper className={resultsMessageWrapperClassNames}>
                <SearchTerm
                    data-test-id={dataTestId}
                    className={searchResultMessageClassNames}
                    aria-label={message}
                    title={message}
                >
                    {message}
                </SearchTerm>
                {resultType === NO_RESULTS ? (
                    <TitleWrapper>{bestSellersProductsTitle}</TitleWrapper>
                ) : null}
            </ResultsMessageWrapper>
        );
    }
}
